import { defineComponent, UnwrapRef } from 'vue'

export const insightsErrorMessage = 'Insights could not be retrieved. Check the API connection and try again.'

/** Every insights payload is paged by week offset, which is what a reload defaults to. */
export interface PagedInsights {
  weekOffset: number
}

export type InsightsFetcher<T extends PagedInsights> = (weekOffset: number) => Promise<T>

/**
 * Builds the shared insights loading mixin: the `insights`/`loading`/`error`/`loadedAt` state and a
 * `loadInsights` method that keeps only the newest request, so a slow response can never overwrite
 * a fresher one.
 */
export function createInsightsLoader<T extends PagedInsights>(fetchInsights: InsightsFetcher<T>) {
  return defineComponent({
    data() {
      return {
        insights: null as T | null,
        loading: true,
        error: '',
        loadedAt: '',
        requestId: 0,
      }
    },
    created() {
      this.loadInsights()
    },
    methods: {
      async loadInsights(weekOffset?: number) {
        const requestId = ++this.requestId
        const requestedWeekOffset = weekOffset ?? this.insights?.weekOffset ?? 0
        this.loading = true
        this.error = ''
        try {
          const insights = await fetchInsights(requestedWeekOffset)
          if (requestId !== this.requestId) return
          // Insights are plain data, so reactive unwrapping is a no-op TypeScript cannot see through a generic.
          this.insights = insights as UnwrapRef<T>
          this.loadedAt = new Date().toLocaleTimeString()
        } catch (error) {
          if (requestId !== this.requestId) return
          this.error = insightsErrorMessage
        } finally {
          if (requestId === this.requestId) this.loading = false
        }
      },
    },
  })
}
